import isEqual from 'lodash/isEqual';

// eslint-disable-next-line import/no-cycle
import { ContinuousScrollingManager } from '../mobile-library/contentFrameInternals/ContinuousScrollingManager';
import { setContext as setSentryContext } from '../mobile-library/contentFrameInternals/errors';
// eslint-disable-next-line import/no-cycle
import getImagePressingInitializer, {
  selectImageElementToHighlight,
} from '../mobile-library/contentFrameInternals/getImagePressingInitializer';
import {
  destroyScrollingManager,
  getScrollingManager,
  initScrollingManager,
} from '../mobile-library/contentFrameInternals/getScrollingManager';
import { HorizontalPaginatedScrollingManager } from '../mobile-library/contentFrameInternals/HorizontalPaginatedScrollingManager';
import { computeHeaderImageColorData } from '../mobile-library/contentFrameInternals/imageUtils';
import { initEndOfReadingButton } from '../mobile-library/contentFrameInternals/initEndOfReading';
import initFailedHighlightsMessage from '../mobile-library/contentFrameInternals/initFailedHighlightsMessage';
import initTableOfContents from '../mobile-library/contentFrameInternals/initTableOfContents';
import { renderTags } from '../mobile-library/contentFrameInternals/initTags';
import {
  addTruncatedArticleMessage, removeTruncatedArticleMessage,
} from '../mobile-library/contentFrameInternals/initTruncatedPaywallArticleMessage';
import {
  destroyYoutubeTeleprompter,
  initYouTubeTeleprompter,
  setYTAutoScrolling,
} from '../mobile-library/contentFrameInternals/initYouTubeTeleprompter';
import { TouchEventDisabledSource } from '../mobile-library/contentFrameInternals/ScrollingManager';
import setupAContainingImg from '../mobile-library/contentFrameInternals/setupAContainingImg';
import setupImages, { destroyImages } from '../mobile-library/contentFrameInternals/setupImages';
import { destroySetupLinks, setupLinks } from '../mobile-library/contentFrameInternals/setupLinks';
import { setupMetadata } from '../mobile-library/contentFrameInternals/setupMetadata';
import { MobileContentFrameWindow } from '../mobile-library/contentFrameInternals/types';
import { VerticalPaginatedScrollingManager } from '../mobile-library/contentFrameInternals/VerticalPaginatedScrollingManager';
import { ThemeCSSMap } from '../mobile-library/css/baseStyles';
import { EndOfReadingButtonHTML } from '../mobile-library/ReaderWebview/WebviewHTML/EndOfReadingButtonHTML';
import { getWebviewInnerStyle, WebviewStyleProps } from '../mobile-library/ReaderWebview/WebviewHTML/styles';
import {
  generateHeaderHtml,
  HeaderDocProps,
  HeaderHTMLProps,
} from '../mobile-library/ReaderWebview/WebviewHTML/WebviewHeaderHTML';
import getWebviewHorizontalMarginCss from '../mobile-library/utils/getWebviewHorizontalMarginCss';
import { Category, EpubToc, LayoutMode, LenientReadingPosition, TtsPosition, YouTubeEnhancedTranscriptStatus } from '../types';
import type { DocumentTag } from '../types/tags';
import { isHTMLElement } from '../typeValidators';
import delay from '../utils/delay';
import { isDevOrTest, isInReactNativeWebView } from '../utils/environment';
import { populateOrRepopulateTTSAbleElements } from './contentFramePortalGateInternalMethods';
import { portalGate as portalGateToForeground } from './portalGates/contentFrame/from/reactNativeWebview';
import { InitialScrollPositionData } from './types/contentFramePuppeteerRelated';
import { makeWebviewLogger } from './utils/makeWebviewLogger';

const logger = makeWebviewLogger(__filename);

declare let window: MobileContentFrameWindow;
const ensureIsMobileWebview = () => {
  if (!isInReactNativeWebView) {
    throw new Error('Cannot call this function outside react native webview');
  }
};

export const mobileSetStyles = ({
  deviceWidth,
  deviceHeight,
  isDigestView,
  isEmailOriginalView,
  layoutMode,
  isPDFDigestPreview,
  isYoutubeVideo,
  font,
  areEpubOriginalStylesEnabled,
  marginTop,
  isHighContrast,
  horizontalMargin,
  fontSize,
  lineHeight,
  shouldJustifyText,
  isRtl,
  theme,
  isTablet,
  platformOS,
  isEinkMode,
}: WebviewStyleProps) => {
  ensureIsMobileWebview();
  window.theme = theme;
  const styleElement = document.getElementById('generatedStyles');
  if (!styleElement) {
    return;
  }
  styleElement.innerHTML = getWebviewInnerStyle({
    deviceWidth,
    deviceHeight,
    isDigestView,
    isEmailOriginalView,
    layoutMode,
    isPDFDigestPreview,
    isYoutubeVideo,
    font,
    areEpubOriginalStylesEnabled,
    marginTop,
    isHighContrast,
    horizontalMargin,
    fontSize,
    lineHeight,
    shouldJustifyText,
    theme,
    isRtl,
    isTablet,
    platformOS,
    isEinkMode,
  });

  document.body.classList.remove(...Object.values(ThemeCSSMap));
  document.body.classList.add(ThemeCSSMap[theme.type]);
  document.body.classList.remove('high-contrast');
  if (isHighContrast) {
    document.body.classList.add('high-contrast');
  }

  const documentTextContent = document.getElementById('document-text-content');
  const topPageSnapshot = document.getElementById('topPageSnapshot');
  const bottomPageSnapshot = document.getElementById('bottomPageSnapshot');
  documentTextContent?.classList.remove('epub-original-styles');
  let topPageSnapshotChild: HTMLElement | null = null;
  let bottomPageSnapshotChild: HTMLElement | null = null;
  if (topPageSnapshot && isHTMLElement(topPageSnapshot?.firstChild)) {
    topPageSnapshotChild = topPageSnapshot.firstChild;
  }
  if (bottomPageSnapshot && isHTMLElement(bottomPageSnapshot?.firstChild)) {
    bottomPageSnapshotChild = bottomPageSnapshot.firstChild;
  }
  topPageSnapshotChild?.classList.remove('epub-original-styles');
  bottomPageSnapshotChild?.classList.remove('epub-original-styles');

  if (areEpubOriginalStylesEnabled) {
    documentTextContent?.classList.add('epub-original-styles');
    topPageSnapshotChild?.classList.add('epub-original-styles');
    bottomPageSnapshotChild?.classList.add('epub-original-styles');
  }

  documentTextContent?.classList.remove('document-content--original-email');
  documentTextContent?.classList.remove('document-content');
  documentTextContent?.classList.add(isEmailOriginalView ? 'document-content--original-email' : 'document-content');

  documentTextContent?.classList.remove('rtl');
  if (isRtl) {
    documentTextContent?.classList.add('rtl');
  }

  computeHeaderImageColorData();

};

export const mobileUpdateEnhancedYoutubeTranscriptStatus = (enhancedYouTubeTranscriptStatus?: YouTubeEnhancedTranscriptStatus) => {
  const container = document.getElementById('youtubeTranscriptStatusContainer');
  if (!container) {
    return;
  }
  container.innerHTML = `${enhancedYouTubeTranscriptStatus === YouTubeEnhancedTranscriptStatus.Pending ? `<div class="enhancing-transcript-message-container"><p class="enhancing-transcript-message">Enhancing transcript...</p></div>` : ''}`;
};

export const mobileSetContentLangAttribute = (languageCode?: string) => {
  const documentTextContent = document.getElementById('document-text-content');
  documentTextContent?.setAttribute('lang', languageCode ?? '');
  const topPageSnapshot = document.getElementById('topPageSnapshot');
  topPageSnapshot?.setAttribute('lang', languageCode ?? '');
  const bottomPageSnapshot = document.getElementById('bottomPageSnapshot');
  bottomPageSnapshot?.setAttribute('lang', languageCode ?? '');
};

export const mobileSetScrollingManagerType = async (layoutMode: LayoutMode) => {
  // Switch up the scrolling manager if we need to
  let currentScrollingManager = getScrollingManager('164 internalPortalGateMethod');
  if (
    layoutMode === LayoutMode.VerticalPagination && !(currentScrollingManager instanceof VerticalPaginatedScrollingManager) ||
    layoutMode === LayoutMode.HorizontalPagination && !(currentScrollingManager instanceof HorizontalPaginatedScrollingManager) ||
    layoutMode === LayoutMode.ContinuousScroll && !(currentScrollingManager instanceof ContinuousScrollingManager)
  ) {
    currentScrollingManager.disableAllTouchEvents(TouchEventDisabledSource.Init);
    const currentScrollPos = currentScrollingManager.computeSerializedPositionFromCenteredElement();
    const currentReadingPosition = currentScrollingManager.readingPosition;
    const currentListeners = currentScrollingManager.scrollListeners;
    const areAnimationsDisabled = currentScrollingManager.smoothAnimationsDisabled;

    destroyScrollingManager();
    currentScrollingManager = initScrollingManager(layoutMode);
    currentScrollingManager.init();
    currentScrollingManager.toggleSmoothAnimations(areAnimationsDisabled);
    for (const func of currentListeners) {
      currentScrollingManager.addScrollListener(func);
    }
    if (window.osType === 'android') {
      await delay(400);
    }
    currentScrollingManager.onResize(true);
    requestAnimationFrame(() => {
      currentScrollingManager.readingPosition = currentReadingPosition;
      currentScrollingManager.enableAllTouchEvents(TouchEventDisabledSource.Init);
      if (currentScrollPos) {
        currentScrollingManager.scrollToSerializedPosition(currentScrollPos.serializedPosition, currentScrollPos.serializedPositionElementOffset);
      }
    });
  }
};
export const mobileCreateHighlightAtTtsPosition = async (ttsPos: TtsPosition) => {
  ensureIsMobileWebview();
  getScrollingManager('197 internalPortalGateMethod').createHighlightAtTtsPosition(ttsPos);
};

export const mobileScrollToTtsPosition = (chunkId: string, ttsPosition: TtsPosition, skipIndicatorUpdate: boolean) => {
  ensureIsMobileWebview();
  const scrollingManager = getScrollingManager('202 internalPortalGateMethod');
  return scrollingManager.scrollToTtsPosition(chunkId, ttsPosition, skipIndicatorUpdate);
};

export const mobileGetScrollDepthFromTtsPosition = async (ttsPosition: TtsPosition) => {
  ensureIsMobileWebview();
  const scrollingManager = getScrollingManager(' 208 internalPortalGateMethod');
  return scrollingManager.getScrollDataForTTSPosition(ttsPosition);
};

export const mobileSetReadingPosition = (pos: LenientReadingPosition | null) => {
  ensureIsMobileWebview();
  getScrollingManager(' 214 internalPortalGateMethod').setReadingPosition(pos);
};
export const mobileReturnToReadingPosition = () => {
  ensureIsMobileWebview();
  getScrollingManager('218 internalPortalGateMethod').returnToReadingPosition();
};

export const mobileScrollToTop = () => {
  ensureIsMobileWebview();
  getScrollingManager(' 223 internalPortalGateMethod').scrollToTop();
};

export const mobileToggleTTSAutoScrolling = (enabled: boolean) => {
  ensureIsMobileWebview();
  getScrollingManager(' 228 internalPortalGateMethod').toggleTTSAutoScrolling(enabled);
};

export const mobilePlayTtsFromCurrentScrollPosition = () => {
  ensureIsMobileWebview();
  getScrollingManager(' 233 internalPortalGateMethod').playTtsFromCurrentScrollPosition();
};

export const updateEndOfReadingText = (text: string) => {
  ensureIsMobileWebview();
  const endOfReadingContainer = document.getElementById('RWEndOfReading');
  if (!endOfReadingContainer) {
    return;
  }
  endOfReadingContainer.innerHTML = EndOfReadingButtonHTML(text);
  initEndOfReadingButton({ os: window.osType });
};

export const mobileUpdateSafeAreaMargins = (margins: {top: number; bottom: number;}) => {
  ensureIsMobileWebview();
  if (isEqual(window.safeAreaMargins, margins)) {
    return;
  }
  window.safeAreaMargins = margins;
  try {
    const scrollingManager = getScrollingManager('updateSafeAreaMargins');
    // window safe area margins have changed, update the scrolling manager
    scrollingManager.setBottomMargin(0);
    scrollingManager.setTopMargin(0);
    scrollingManager.onResize(true);
  } catch {
    // Nothing, we just don't have a scrolling manager yet
  }
};

export const mobileSelectImageElementToHighlight = async (imageId: string) => {
  ensureIsMobileWebview();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const image = document.getElementById(imageId) as HTMLImageElement; // claude eslint: getElementById returns HTMLElement | null; callers pass an ID that always refers to an img element; null is checked immediately after
  if (image === null) {
    throw new Error('highlightImage - couldnt find image with provided id');
  }
  return selectImageElementToHighlight(image);
};

export const mobileChangeAutohighlightingStatus = async (isEnabled: boolean) => {
  ensureIsMobileWebview();
  window.isAutoHighlightingEnabled = isEnabled;
  if (!isEnabled) {
    const textContent = document.getElementById('document-text-content');
    if (textContent) {
      textContent.classList.remove('document-text-content--would-auto-highlight-selection');
    }
  }
};

export const mobileChangeQuickLookupStatus = async (isUsable: boolean) => {
  ensureIsMobileWebview();
  window.isQuickLookupUsable = isUsable;
};

let lastSeenDocForHeader: HeaderDocProps | null;
export const mobileSetHeaderContent = ({ doc, styleProps, tags, isSummaryContainerInitiallyExpanded, showSummary }: HeaderHTMLProps & {tags: DocumentTag[];}) => {
  ensureIsMobileWebview();
  const headerContainer = document.getElementById('RWHeaderContainer');
  if (!headerContainer) {
    throw new Error('No header container found');
  }
  if (!isEqual(lastSeenDocForHeader, doc)) {
    destroyImages();
    headerContainer.innerHTML = generateHeaderHtml({
      styleProps,
      doc,
      isSummaryContainerInitiallyExpanded,
      showSummary,
    });
    setupImages();
    setupMetadata();

    lastSeenDocForHeader = doc !== null ? { ...doc } : null;
    if (doc === null) {
      headerContainer.classList.add('transparent');
    } else {
      headerContainer.classList.remove('transparent');
    }
  }
  if (isEqual(tags, lastSeenTags)) {
    return;
  }
  renderTags(tags);
  lastSeenTags = Array.from(tags);
};

let lastSeenTags: DocumentTag[] = [];
export const mobileRenderTags = (tags: DocumentTag[]) => {
  if (isEqual(tags, lastSeenTags)) {
    return;
  }
  renderTags(tags);
  lastSeenTags = Array.from(tags);
};

export const mobileSetSentryContext = (userEmail: string, docId: string, title: string, appVersion: string) => {
  ensureIsMobileWebview();
  setSentryContext({ email: userEmail, docId, title, appVersion });
};

export const mobileUpdateSummary = (summary: string) => {
  ensureIsMobileWebview();

  const summaryContainer = document.getElementById('summary-text');
  if (!summaryContainer) {
    return;
  }
  summaryContainer.innerText = summary;

  const parentContainer = document.getElementById('summary-container');
  if (parentContainer) {
    if (parentContainer.style.maxHeight) {
      parentContainer.style.maxHeight = `${parentContainer.scrollHeight}px`;
    }
  }
};

export const mobileToggleYTAutoScrolling = (enabled: boolean) => {
  ensureIsMobileWebview();
  setYTAutoScrolling(enabled);
};

export const mobileTogglePaginationAnimationsDisabled = (disabled: boolean) => {
  ensureIsMobileWebview();
  const scrollingManager = getScrollingManager('346 internalPortalGateMethod');
  scrollingManager.toggleSmoothAnimations(disabled);
  scrollingManager.smoothAnimationsDisabled = disabled;
};
export const mobileTogglePaginationHapticsOnScrollEnabled = (enabled: boolean) => {
  ensureIsMobileWebview();
  const scrollingManager = getScrollingManager('352 internalPortalGateMethod');
  scrollingManager.hapticsOnScrollEnabled = enabled;
};

export const mobileScrollToNextPage = () => {
  ensureIsMobileWebview();
  const scrollingManager = getScrollingManager('358 internalPortalGateMethod');
  if (
    !(scrollingManager instanceof VerticalPaginatedScrollingManager)
  ) {
    return;
  }
  scrollingManager.queueScrollToPageEvent({ direction: 'down', velocity: 0, disableAnimation: true });
};

export const mobileScrollToPreviousPage = () => {
  ensureIsMobileWebview();
  const scrollingManager = getScrollingManager(' 369 internalPortalGateMethod');
  if (
    !(scrollingManager instanceof VerticalPaginatedScrollingManager)
  ) {
    return;
  }
  scrollingManager.queueScrollToPageEvent({ direction: 'up', velocity: 0, disableAnimation: true });
};

const BOTTOM_BAR_PLUS_TWO_LINES_OF_TEXT_APPROX_HEIGHT = 110;

export const mobileScrollApproxOnePage = (direction: 'up' | 'down') => {
  ensureIsMobileWebview();
  const scrollingManager = getScrollingManager('382 internalPortalGateMethod');
  if (!(scrollingManager instanceof ContinuousScrollingManager)) {
    return;
  }
  const scrollOffset = window.innerHeight - BOTTOM_BAR_PLUS_TWO_LINES_OF_TEXT_APPROX_HEIGHT;
  const positionToScroll =
    direction === 'up'
      ? scrollingManager.currentScrollValue - scrollOffset
      : scrollingManager.currentScrollValue + scrollOffset;
  scrollingManager.scrollToPosition(positionToScroll);
};

export async function mobileInitTableOfContents(docCategory: Category, toc: EpubToc[] | undefined) {
  return initTableOfContents(docCategory === Category.EPUB, toc);
}

export async function mobileInitDocument(
  layoutMode: LayoutMode,
  initialPositionData: InitialScrollPositionData,
) {
  destroyScrollingManager();
  const scrollingManager = initScrollingManager(layoutMode);
  if (isDevOrTest) {
    document.body.classList.add('isDev');
  }
  const documentRoot = document.querySelector('.document-root');
  if (!documentRoot) {
    throw new Error('No .document-root found');
  }
  const contentRoot = document.querySelector('#document-text-content');
  if (!contentRoot) {
    throw new Error('No #document-text-content found');
  }

  setupImages();
  getImagePressingInitializer({ os: window.osType })();
  setupAContainingImg();
  setupLinks();
  initFailedHighlightsMessage();

  scrollingManager.disableScrollEvents();

  const { initialTtsPosition, initialReadPosition, initialScrollPosition, landmarks, pageList } = initialPositionData;
  initYouTubeTeleprompter({
    scrollDepth: initialScrollPosition.scrollDepth ?? 0,
    isAutoScrollEnabled: false,
  });
  scrollingManager.init();
  scrollingManager._debugInitialScrollPosition = initialPositionData;
  scrollingManager.landmarks = landmarks;
  scrollingManager.pageList = pageList;
  requestAnimationFrame(async () => {
    let didScrollToTtsPosition = false;
    if (initialTtsPosition) {
      try {
        await scrollingManager.scrollToTtsPosition(initialTtsPosition.chunkId, initialTtsPosition, false);
        didScrollToTtsPosition = true;
      } catch (error) {
        logger.error('Could not scroll to TTS position', {
          error,
          message: error instanceof Error ? error.message : error,
          initialTtsPosition,
          initialPositionData,
        });
      }
    }
    if (!didScrollToTtsPosition && initialScrollPosition) {
      if (initialScrollPosition.scrollDepth === 0) {
        await scrollingManager.scrollToStartOfDocument(landmarks, pageList);
      } else {
        await scrollingManager.scrollToReadingPosition(initialScrollPosition);
      }
    }
    scrollingManager.setReadingPosition(initialReadPosition);

    window.documentLoaded = true;
    window.portalGateToForeground.emit('report-is-chunked-epub', scrollingManager.isChunked);

    populateOrRepopulateTTSAbleElements(scrollingManager.getChunkContainerByIndex(scrollingManager.currentChunkIndex));
    window.portalGateToForeground.emit('is-document-ttsable', (window.tts?.ttsAbleElements?.length ?? 0) > 0);

    setTimeout(() => {
      scrollingManager.enableScrollEvents();
    }, 200);

    documentRoot.classList.remove('transparent');
    await portalGateToForeground.emit('doc-content-loaded', {
      initialScrollDepth: initialScrollPosition.scrollDepth ?? 0,
      initialReadingDepth: initialReadPosition.scrollDepth ?? 0,
    });
  });
}
export const setHorizontalMargin = async (margin: number, isTablet: boolean) => {
  ensureIsMobileWebview();
  const style = document.getElementById('RWMarginStyle');
  if (!style) {
    return;
  }

  style.innerHTML = getWebviewHorizontalMarginCss(margin, isTablet).css;
};

export const mobileRetryLoadImages = () => {
  ensureIsMobileWebview();
  if (!window.reloadImagesThatFailedToLoad) {
    return;
  }
  window.reloadImagesThatFailedToLoad();
};

export const refreshPaginationPageSnapshots = async () => {
  ensureIsMobileWebview();
  getScrollingManager('480 internalPortalGateMethod').refreshPageSnapshotsForCurrentPage();
};

export const forceScroll = async () => {
  ensureIsMobileWebview();
  const scrollingManager = getScrollingManager('485 internalPortalGateMethod');
  scrollingManager.enableScrollEvents();
  scrollingManager.onScrollStart();
  scrollingManager.onScroll();
  scrollingManager.onScrollEnd();
};

export const mobileSetIsSlowChunkLoadingEnabled = async (enabled: boolean) => {
  window.isSlowChunkLoadingEnabled = enabled;
};

export const mobileSetIsScrollingManagerFPSLoggingEnabled = async (enabled: boolean) => {
  window.isScrollingManagerFPSLoggingEnabled = enabled;
};

export const mobileSetCenteredElementDebugEnabled = async (enabled: boolean) => {
  window.isCenteredElementDebugEnabled = enabled;
};

export const toggleTruncatedArticleBanner = async (articleUrl: string | null) => {
  if (articleUrl) {
    addTruncatedArticleMessage(articleUrl);
  } else {
    removeTruncatedArticleMessage();
  }
};

export const instantlyScrollPageUp = async () => {
  ensureIsMobileWebview();
  const scrollingManager = getScrollingManager('scrollPageUp');
  scrollingManager.instantlyScrollPageUp();
};
export const instantlyScrollPageDown = async () => {
  ensureIsMobileWebview();
  const scrollingManager = getScrollingManager('scrollPageDown');
  scrollingManager.instantlyScrollPageDown();
};

export const mobileDestroyContent = async () => {
  destroyYoutubeTeleprompter();
  destroyScrollingManager();
  destroyImages();
  destroySetupLinks();
  removeTruncatedArticleMessage();
  const documentTextContent = document.getElementById('document-text-content');
  const documentRoot = document.querySelector('.document-root');
  if (documentRoot) {
    documentRoot.classList.remove('transparent');
    documentRoot.classList.add('transparent');
  }
  if (documentTextContent) {
    documentTextContent.innerHTML = '';
  }
};

export const mobileSetDragPreventionEnabled = (enabled: boolean) => {
  ensureIsMobileWebview();
  const documentContent = document.getElementById('document-text-content');
  if (!documentContent) {
    return;
  }
  if (enabled) {
    documentContent.classList.add('drag-prevention-enabled');
  } else {
    documentContent.classList.remove('drag-prevention-enabled');
  }
};
